// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Durable per-round snapshot of the hole screen's bootstrap data so a cold
// app start without connectivity can still render the screen (players, hole
// entries, hole events, subgames, ...). Storage follows the repo's dual
// pattern: localStorage on web, AsyncStorage on native. The in-memory caches
// in useHoleData stay first-priority; this is the offline/cold-start fallback.

import type {
  GameDefinitionSummary,
  HoleEntry,
  HoleEventEntry,
  Player,
  RoundSubgameEntry,
  Subgame,
  TeamSummary,
} from '../features/hole/types';
import type { EclecticSection, FreezeSection } from '../features/tour-games/types';

type AsyncStorageLike = {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  getAllKeys?(): Promise<readonly string[]>;
};

export type RoundCacheData = {
  players?: Player[];
  tourId?: string;
  teams?: TeamSummary[];
  metadata?: { gameDefinitionIds: string[]; holesCount: number };
  holeEntries?: HoleEntry[];
  holeEvents?: HoleEventEntry[];
  roundSubgames?: RoundSubgameEntry[];
  freeze?: FreezeSection;
  eclectic?: EclecticSection;
};

export type UserCacheData = {
  subgames?: Subgame[];
  gameDefinitions?: GameDefinitionSummary[];
  loggedInPlayerId?: string | null;
};

const STORAGE_VERSION = 1;
const ROUND_KEY_PREFIX = `goffy:round-cache:v${STORAGE_VERSION}:`;
const USER_KEY_PREFIX = `goffy:round-cache-user:v${STORAGE_VERSION}:`;
const MAX_CACHED_ROUNDS = 3;
const WRITE_DEBOUNCE_MS = 500;

const roundKeyFor = (userId: string | null | undefined, roundId: string) =>
  `${ROUND_KEY_PREFIX}${userId ?? 'anonymous'}:${roundId}`;

const userKeyFor = (userId: string | null | undefined) => `${USER_KEY_PREFIX}${userId ?? 'anonymous'}`;

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

const getLocalStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

let storageOverride: AsyncStorageLike | null = null;

const readRaw = async (key: string): Promise<string | null> => {
  if (storageOverride) return storageOverride.getItem(key);
  const local = getLocalStorage();
  if (local) return local.getItem(key);
  if (asyncStorage) return asyncStorage.getItem(key);
  return null;
};

const writeRaw = async (key: string, value: string): Promise<void> => {
  if (storageOverride) return storageOverride.setItem(key, value);
  const local = getLocalStorage();
  if (local) {
    local.setItem(key, value);
    return;
  }
  if (asyncStorage) await asyncStorage.setItem(key, value);
};

const removeRaw = async (key: string): Promise<void> => {
  if (storageOverride) return storageOverride.removeItem(key);
  const local = getLocalStorage();
  if (local) {
    local.removeItem(key);
    return;
  }
  if (asyncStorage) await asyncStorage.removeItem(key);
};

const listKeys = async (): Promise<string[]> => {
  if (storageOverride?.getAllKeys) return [...(await storageOverride.getAllKeys())];
  const local = getLocalStorage();
  if (local) return Object.keys(local);
  if (asyncStorage?.getAllKeys) return [...(await asyncStorage.getAllKeys())];
  return [];
};

type RoundEnvelope = { version: number; savedAt: string; data: RoundCacheData };
type UserEnvelope = { version: number; savedAt: string; data: UserCacheData };

const parseEnvelope = <T extends { version: number }>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as T;
    if (!parsed || typeof parsed !== 'object' || parsed.version !== STORAGE_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

// Pending merged patches keyed by storage key, flushed on a debounce so WS
// bursts (hole.update per score) don't hammer storage.
const pendingRoundPatches = new Map<string, { userId: string | null; roundId: string; patch: RoundCacheData }>();
const pendingUserPatches = new Map<string, UserCacheData>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const flushPatches = async () => {
  flushTimer = null;
  const roundPatches = [...pendingRoundPatches.entries()];
  pendingRoundPatches.clear();
  const userPatches = [...pendingUserPatches.entries()];
  pendingUserPatches.clear();

  for (const [key, { patch }] of roundPatches) {
    try {
      const existing = parseEnvelope<RoundEnvelope>(await readRaw(key));
      const envelope: RoundEnvelope = {
        version: STORAGE_VERSION,
        savedAt: new Date().toISOString(),
        data: { ...(existing?.data ?? {}), ...patch },
      };
      await writeRaw(key, JSON.stringify(envelope));
    } catch {
      // Storage full/unavailable: skip silently, the cache is best-effort.
    }
  }

  for (const [key, patch] of userPatches) {
    try {
      const existing = parseEnvelope<UserEnvelope>(await readRaw(key));
      const envelope: UserEnvelope = {
        version: STORAGE_VERSION,
        savedAt: new Date().toISOString(),
        data: { ...(existing?.data ?? {}), ...patch },
      };
      await writeRaw(key, JSON.stringify(envelope));
    } catch {
      // Best-effort.
    }
  }

  if (roundPatches.length > 0) {
    void pruneRoundCaches();
  }
};

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    void flushPatches();
  }, WRITE_DEBOUNCE_MS);
};

export const patchRoundCache = (
  userId: string | null | undefined,
  roundId: string | null | undefined,
  patch: RoundCacheData
): void => {
  if (!roundId) return;
  const key = roundKeyFor(userId, roundId);
  const pending = pendingRoundPatches.get(key);
  pendingRoundPatches.set(key, {
    userId: userId ?? null,
    roundId,
    patch: { ...(pending?.patch ?? {}), ...patch },
  });
  scheduleFlush();
};

export const patchUserCache = (userId: string | null | undefined, patch: UserCacheData): void => {
  const key = userKeyFor(userId);
  const pending = pendingUserPatches.get(key);
  pendingUserPatches.set(key, { ...(pending ?? {}), ...patch });
  scheduleFlush();
};

export const readRoundCache = async (
  userId: string | null | undefined,
  roundId: string | null | undefined
): Promise<RoundCacheData | null> => {
  if (!roundId) return null;
  const envelope = parseEnvelope<RoundEnvelope>(await readRaw(roundKeyFor(userId, roundId)));
  if (!envelope || !envelope.data || typeof envelope.data !== 'object') return null;
  return envelope.data;
};

export const readUserCache = async (userId: string | null | undefined): Promise<UserCacheData | null> => {
  const envelope = parseEnvelope<UserEnvelope>(await readRaw(userKeyFor(userId)));
  if (!envelope || !envelope.data || typeof envelope.data !== 'object') return null;
  return envelope.data;
};

export const pruneRoundCaches = async (keep: number = MAX_CACHED_ROUNDS): Promise<void> => {
  try {
    const keys = (await listKeys()).filter((key) => key.startsWith(ROUND_KEY_PREFIX));
    if (keys.length <= keep) return;
    const withSavedAt = await Promise.all(
      keys.map(async (key) => {
        const envelope = parseEnvelope<RoundEnvelope>(await readRaw(key));
        return { key, savedAt: envelope?.savedAt ?? '' };
      })
    );
    withSavedAt
      .sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1))
      .slice(keep)
      .forEach(({ key }) => {
        void removeRaw(key);
      });
  } catch {
    // Best-effort.
  }
};

// Test hooks — not for production use.
export const __setRoundCacheStorageForTests = (storage: AsyncStorageLike | null) => {
  storageOverride = storage;
};

export const __flushRoundCacheForTests = async () => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  await flushPatches();
};
